import { FC, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Input,
  Button,
  FormControl,
  FormLabel,
  Select,
  Option,
  Stack,
  Textarea,
  Switch,
} from '@mui/joy';
import { ModalTemplate } from '../../features/components/ui/Modal';
import { FileUpload } from '../FileUpload/FileUpload';
import { setNotify } from '../../features/notifications/notifySlice';
import { ASSETS_TYPES } from '../../config/consts';
import { useCreateAssetMutation } from '../../app/services/admin';
import { TCreateAsset, TAssetType } from '../../config/requestTypes';

interface ICreateNewAssetModal {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

export const CreateNewAssetModal: FC<ICreateNewAssetModal> = ({
  openModal,
  setOpenModal,
}) => {
  const dispatch = useDispatch();
  const [type, setType] = useState<TAssetType>(ASSETS_TYPES[0].value);
  const [description, setDescription] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [decimalPlaces, setDecimalPlaces] = useState('');
  const [assetIcon, setAssetIcon] = useState<File | null>(null);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  const [createAsset, { isSuccess, isError, isLoading }] =
    useCreateAssetMutation();

  const handleChangeComissionPercent = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = e.target.value;
    if (val.length <= 3) setCommissionPercent(val);
  };

  const handleChangeDecimalPlaces = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = e.target.value;
    if (val) setDecimalPlaces(val);
  };

  const setImageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      setAssetIcon(selectedFile);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        setNotify({
          message: 'Актив успешно создан!',
          type: 'success',
          duration: 2000,
        }),
      );

      setOpenModal(false);
    }

    if (isError) {
      dispatch(
        setNotify({
          message: 'Ошибка добавления актива!',
          type: 'danger',
          duration: 2000,
        }),
      );
    }
  }, [isSuccess, isError]);

  return (
    <ModalTemplate
      open={openModal}
      setOpen={setOpenModal}
      title="Заполните форму нового актива"
    >
      <Switch
        size="lg"
        color="success"
        variant="soft"
        onChange={(e) => setIsHidden(!e.target.checked)}
        defaultChecked
        sx={{
          // '--Switch-trackWidth': '52px',
          // '--Switch-trackHeight': '30px',
          // '--Switch-thumbSize': '24px',
          '--Switch-trackBackground': '#7a7a7a',
          '--Switch-thumbBackground': '#383838',
          '&.Mui-checked': {
            '--Switch-trackBackground': '#6366f1',
            '--Switch-thumbBackground': '#00038d',
            '&:hover': {
              '--Switch-trackBackground': '#6366f1',
              '--Switch-thumbBackground': '#00038d',
            },
          },
          '&:hover': {
            '--Switch-trackBackground': '#d1d5db',
            '--Switch-thumbBackground': '#666565',
          },
        }}
        className="createAssetModal__switchbox"
      />
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
        onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();

          const formElements = new FormData(event.currentTarget);
          const formData = Object.fromEntries(formElements.entries());
          const keyMapNotSet = {
            symbol: 'Необходимо указать биржевой код актива!',
            type: 'Необходимо указать тиа актива!',
            title: 'Необходимо указать название актива!',
            subtitle: 'Необходимо указать подзаголовок актива!',
          };
          const data: TCreateAsset = {
            symbol: String(formData.symbol),
            type,
            title: String(formData.title),
            subtitle: String(formData.subtitle),
            description,
            commissionPercent: +commissionPercent,
            decimalPlaces: +decimalPlaces,
            image: assetIcon,
            isHidden,
          };

          Object.entries(data).forEach(([key, value]) => {
            if (!value) {
              dispatch(
                setNotify({
                  message: keyMapNotSet[key as keyof typeof keyMapNotSet],
                  type: 'danger',
                  duration: 2000,
                }),
              );
              return;
            }
          });

          createAsset(data);
        }}
      >
        <FormControl>
          <FormLabel>Название</FormLabel>
          <Input name="title" type="text" required />
        </FormControl>
        <FormControl>
          <FormLabel>Подзаголовок</FormLabel>
          <Input name="subtitle" type="text" required />
        </FormControl>
        <FormControl>
          <FormLabel>Тип</FormLabel>
          <Select
            value={type}
            placeholder="Выберите тип актива..."
            onChange={(event, newValue) => {
              if (newValue) setType(newValue);
            }}
          >
            {ASSETS_TYPES.map((el, i) => (
              <Option value={el.value} key={`${el.value}-${i}`}>
                {el.label}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Код</FormLabel>
          <Input name="symbol" type="text" required />
        </FormControl>
        <FormControl>
          <FormLabel>Описание</FormLabel>
          <Textarea
            placeholder="Введите текст..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minRows={3}
            maxRows={10}
            // autoFocus
          />
        </FormControl>
        <Stack direction="row" spacing={2}>
          <FormControl style={{ width: '100%' }}>
            <FormLabel>Процент комиссии</FormLabel>
            <Input
              name="commissionPercent"
              type="number"
              value={commissionPercent}
              onChange={handleChangeComissionPercent}
              slotProps={{
                input: {
                  maxLength: 3,
                  min: 0,
                  max: 100,
                  step: 0.01,
                },
              }}
            />
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <FormLabel>Количество знаков после точки</FormLabel>
            <Input
              name="decimalPlaces"
              type="number"
              value={decimalPlaces}
              onChange={handleChangeDecimalPlaces}
              slotProps={{
                input: {
                  min: 0,
                },
              }}
            />
          </FormControl>
        </Stack>
        <FormControl>
          <FileUpload label="Иконка (только SVG)" onChange={setImageHandler} />
        </FormControl>

        <Button
          type="submit"
          style={{
            width: '100%',
            marginTop: '10px',
          }}
          loading={isLoading}
        >
          Создать актив
        </Button>
      </form>
    </ModalTemplate>
  );
};
